// Mana-curve bar chart. Shared between DeckEdit (live editor panel) and
// DeckDetail (saved deck page). The chart itself is fixed-pixel height
// because a % bar inside a flex column with items-end collapsed to zero
// without a resolved parent height.

import type { DeckEntry } from "../lib/api";
import type { CardMeta } from "../lib/api";

export function computeManaCurve(
  mainboard: DeckEntry[],
  meta: Record<string, CardMeta> | undefined,
): { curve: Record<number, number>; nonlandTotal: number } {
  const curve: Record<number, number> = {};
  let nonlandTotal = 0;
  for (const e of mainboard) {
    const m = meta?.[e.name.toLowerCase()];
    if (!m) continue;
    if (m.type_line.includes("Land")) continue;
    const bucket = Math.min(7, Math.round(m.cmc));
    curve[bucket] = (curve[bucket] ?? 0) + e.count;
    nonlandTotal += e.count;
  }
  return { curve, nonlandTotal };
}

export default function ManaCurve({
  curve,
}: {
  curve: Record<number, number>;
}) {
  const BARS_MAX_PX = 56;
  const buckets = [0, 1, 2, 3, 4, 5, 6, 7] as const;
  const max = Math.max(1, ...buckets.map((b) => curve[b] ?? 0));
  return (
    <div className="flex items-end gap-1.5">
      {buckets.map((b) => {
        const n = curve[b] ?? 0;
        const barH = n > 0 ? Math.max(2, Math.round((n / max) * BARS_MAX_PX)) : 0;
        return (
          <div
            key={b}
            className="flex-1 flex flex-col items-center min-w-0"
            title={`${n} at CMC ${b === 7 ? "7+" : b}`}
          >
            <div
              className="w-full flex items-end"
              style={{ height: BARS_MAX_PX }}
            >
              <div
                className="w-full bg-accent/60 rounded-sm"
                style={{ height: barH }}
              />
            </div>
            <div className="font-mono text-[10px] text-fg-subtle tabular-nums pt-1">
              {b === 7 ? "7+" : b}
            </div>
          </div>
        );
      })}
    </div>
  );
}
